import {
  ClipboardIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  CloudArrowUpIcon,
} from '@heroicons/react/24/outline';
import { useState, useEffect } from 'react';
import {
  createOnPremPimsInviteLink,
  getOnPremPimsInviteLink,
  pingOnPremPimsAccount,
} from '../../ServerActions';
import { useAuth } from '@clerk/clerk-react';
import { getPimsDisplayName } from '../../utils/PimsUtils';
import { Spinner } from '../../utils/Spinner';
import Modal from '../../../../extension/Components/common/Modal';

interface Props {
  open: boolean;
  onClose: () => void;
  onComplete: () => void;
  status: 'pending' | 'completed' | undefined;
  pims: string;
}

export default function OnPremSetupModal({
  open,
  onClose,
  onComplete,
  status,
  pims,
}: Props) {
  const { getToken } = useAuth();
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [expireTime, setExpireTime] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifySuccess, setVerifySuccess] = useState<boolean | null>(null);
  const [copied, setCopied] = useState(false);

  const pimsDisplayName = getPimsDisplayName(pims);

  /* ------------------------- misc helpers ------------------------ */

  useEffect(() => {
    if (!open) setCopied(false);
  }, [open]);

  const handleCopy = () => {
    if (!inviteUrl) return;
    navigator.clipboard.writeText(inviteUrl)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2_000);
      })
      .catch((err) => {
        console.error('Failed to copy:', err);
      });
  };

  /* ---------------- fetch existing invite link ------------------- */

  useEffect(() => {
    if (!open) return;

    (async () => {
      setVerifySuccess(null);

      try {
        const jwt = (await getToken({ template: 'supabase' })) ?? '';

        if (status === 'completed') {
          /* nothing to fetch if we're already completed */
          return;
        }

        if (status === 'pending') {
          setLoading(true);
          const response = await getOnPremPimsInviteLink(jwt);
          setInviteUrl(response.url);
          setExpireTime(response.expire_time);
          setLoading(false);
        }
      } catch (e: any) {
        setLoading(false);
      }
    })().catch((e) => {
      setLoading(false);
    });
  }, [open, status, getToken]);

  /* -------------------- generate new link ------------------------ */

  async function handleGenerateLink() {
    setGenerating(true);
    try {
      const jwt = (await getToken({ template: 'supabase' })) ?? '';
      const response = await createOnPremPimsInviteLink(pims, jwt);
      setInviteUrl(response.uri);
      setExpireTime(response.expire_time);
    } catch (e: any) {
      console.error(e);
    } finally {
      setGenerating(false);
    }
  }

  /* -------------------- verify connection ------------------------ */

  async function handleVerify() {
    setVerifying(true);
    setVerifySuccess(null);
    try {
      const jwt = (await getToken({ template: 'supabase' })) ?? '';
      const result = await pingOnPremPimsAccount(pims, jwt);

      if (result.status === 'success') {
        setVerifySuccess(true);
        if (status === 'pending' || result.enabled) onComplete();
      } else {
        setVerifySuccess(false);
        console.error(result.message ?? 'Verification failed');
      }
    } catch (e: any) {
      setVerifySuccess(false);
      console.error(e.message ?? 'Verification failed');
    } finally {
      setVerifying(false);
    }
  }

  /* ------------------------------ UI ----------------------------- */

  return (
    <Modal show={open} onClose={onClose}>
      <div>
        {/* ───────── Header ───────── */}
        <div>
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-blue-100">
            <CloudArrowUpIcon className="h-6 w-6 text-blue-600" aria-hidden="true" />
          </div>
          <div className="mt-3 text-center sm:mt-5">
            <h3 className="text-base font-semibold leading-6 text-gray-900">
              {pimsDisplayName} Connection Setup
            </h3>
          </div>
        </div>

        <div className="mt-4">
          {/* ───────── Loading while fetching existing link ───────── */}
          {loading && (
            <div className="flex items-center justify-center py-4">
              <Spinner size="h-8 w-8" timer={false} />
              <p className="ml-3 text-sm text-gray-500">Loading installation link…</p>
            </div>
          )}

          {/* ───────── Step‑0 (generate link) ───────── */}
          {!inviteUrl && status !== 'completed' && !generating && !loading && (
            <div className="text-center">
              <p className="mt-2 text-gray-500">
                Click the button below to generate an installation link for {pimsDisplayName}.
                <br />
                <span className="font-medium text-gray-500">
                  You&apos;ll need to open this link on the {pimsDisplayName} server and complete the
                  installation process.
                </span>
              </p>
              <button
                onClick={handleGenerateLink}
                disabled={generating}
                className="mt-4 inline-flex w-full items-center justify-center rounded-md bg-blue-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
              >
                Generate Installation Link
              </button>
            </div>
          )}

          {/* ───────── "generating…" spinner ───────── */}
          {generating && (
            <div className="flex items-center justify-center py-4">
              <Spinner size="h-8 w-8" timer={false} />
              <p className="ml-3 text-sm text-gray-500">Generating link…</p>
            </div>
          )}

          {/* ───────── Invite link + verify (pending) ───────── */}
          {inviteUrl && status !== 'completed' && !verifySuccess &&  (
            <div className="space-y-4">
              {/* Step 1 */}
              <div className="space-y-3">
                <p className="text-sm text-gray-700">
                  <span className="font-medium">Step&nbsp;1:</span> Copy the link below and open it on the&nbsp;
                  {pimsDisplayName} server.
                </p>
                <p className="text-center text-sm font-medium text-gray-700">
                  Complete the installation process until the end.
                </p>

                <div className="flex flex-col gap-y-2">
                  <div className="flex items-center gap-x-2 rounded-md border border-gray-300 bg-gray-50 p-2 text-sm">
                    <span className="truncate">{inviteUrl}</span>
                    <button
                      onClick={handleCopy}
                      className={`rounded p-1.5 transition-colors duration-200 ${
                        copied
                          ? 'bg-green-100 text-green-600 hover:bg-green-200'
                          : 'hover:bg-gray-200'
                      }`}
                      title={copied ? 'Copied!' : 'Copy'}
                      disabled={copied}
                    >
                      {copied ? (
                        <CheckCircleIcon className="h-4 w-4" />
                      ) : (
                        <ClipboardIcon className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  {expireTime && (
                    <p className="text-xs text-gray-500">
                      This link will expire on {new Date(expireTime).toLocaleString()}
                    </p>
                  )}
                </div>
              </div>

              {/* Step 2 */}
              <div>
                <p className="text-sm text-gray-700">
                  <span className="font-medium">Step&nbsp;2:</span> After completing the installation on the
                  server,&nbsp;return here and click&nbsp;<strong>Verify&nbsp;Connection</strong>.
                </p>
                <button
                  onClick={handleVerify}
                  disabled={verifying}
                  className="mt-3 inline-flex w-full items-center justify-center rounded-md bg-blue-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                >
                  {verifying ? (
                    <>
                      <Spinner size="h-5 w-5" timer={false} />
                      <span className="ml-2">Verifying…</span>
                    </>
                  ) : (
                    'Verify Connection'
                  )}
                </button>
              </div>
            </div>
          )}

          {/* ───────── Verify (completed link) ───────── */}
          {status === 'completed' && !verifySuccess && (
            <div className="space-y-4">
              <p className="text-sm text-gray-700">
                Your {pimsDisplayName} connection is already set up. Click&nbsp;
                <strong>Verify&nbsp;Connection</strong> to run a health‑check on the linked account.
              </p>

              <button
                onClick={handleVerify}
                disabled={verifying}
                className="inline-flex w-full items-center justify-center rounded-md bg-blue-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {verifying ? (
                  <>
                    <Spinner size="h-5 w-5" timer={false} />
                    <span className="ml-2">Verifying…</span>
                  </>
                ) : (
                  'Verify Connection'
                )}
              </button>
            </div>
          )}

          {/* ───────── success / failure messages ───────── */}
          {verifySuccess && (
            <div className="mt-4 flex items-center justify-center gap-x-2 rounded-md border border-green-200 bg-green-50 p-3 text-green-800">
              <CheckCircleIcon className="h-5 w-5 flex-shrink-0" />
              <span className="text-sm font-semibold">Connection successful!</span>
            </div>
          )}

          {verifySuccess === false && (
            <div className="mt-4 flex items-center justify-center gap-x-2 rounded-md border border-red-200 bg-red-50 p-3 text-red-800">
              <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
              <span className="text-sm font-semibold">
                Connection failed — please confirm the link step was completed.
              </span>
            </div>
          )}
        </div>

        {/* ───────── footer ───────── */}
        <div className="mt-5 sm:mt-6">
          <button
            onClick={onClose}
            className="inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
}